import useAppContext from '@groupefungo/react-apollo.core/app/UseContext';
import useCommonFragment from '../../../../../base/hooks/CommonFragments';
import useOrderFragment from '../../orders/hooks/useOrderFragment';
import useMeasurementFragment from '../../measurements/hooks/useMeasurementFragment';
import useEventGql from '../../events/hooks/useEventGql';

export default () => {
  const { gql, useQuery, useMutation } = useAppContext();
  const { PhoneNumberFragment, AddressFragment, PageInfoFragment, InspirationFragment } = useCommonFragment();
  const { OrderFragment } = useOrderFragment();
  const { MeasurementFragment } = useMeasurementFragment();
  const { EventsListFragment } = useEventGql();

  const ClientReducedFragment = gql`
  fragment ClientReducedFragment on Client {
    email
    fullName
    firstName
    lastName
    isVip
    dressSize
    preferredLang
    events {
      id
      gid
      ...EventFragment
    }
    clientsEvents {
      id
      event {
        id
        eventTitle
        eventDate
      }
    }
    phoneNumbers {
      id
      gid
      ...PhoneNumberFragment
    }
    address {
      ...AddressFragment
    }
    orders {
      ...OrderFragment
    }
    inspirations {
      ...InspirationFragment
    }
    measurement {
      ...MeasurementFragment
    }
    comments {
    id
    text
    createdAt
    writtenBy {
        email
        firstName
        lastName
        roles {
          id
          name
        }
      }
    }
  }
  ${PhoneNumberFragment}
  ${AddressFragment}
  ${OrderFragment}
  ${InspirationFragment}
  ${MeasurementFragment}
  ${EventsListFragment}
  `;
  const ClientNameOnlyFragment = gql`
  fragment ClientNameOnlyFragment on Client {
    fullName
    firstName
    lastName
  }
  `;

  const ClientFragment = gql`
  fragment ClientFragment on Client {
    email
    fullName
    firstName
    lastName
    ecommerceId
    referral
    dressSize
    isVip
    preferredLang
    allowSms
    allowEmail
    appointmentsGids
    events {
      id
      gid
      ...EventFragment
    }
    clientsEvents {
      id
      event {
        id
        eventTitle
        eventDate
      }
    }
    phoneNumbers {
      id
      gid
      ...PhoneNumberFragment
    }
    address {
      ...AddressFragment
    }
    orders {
      ...OrderFragment
    }
    inspirations {
      ...InspirationFragment
    }
    measurement {
      ...MeasurementFragment
    }
    alterationRange {
      id
      clientId
      firstAlterationStart
      firstAlterationEnd
      secondAlterationStart
      secondAlterationEnd
      thirdAlterationStart
      thirdAlterationEnd
    }
  }
  ${PhoneNumberFragment}
  ${AddressFragment}
  ${OrderFragment}
  ${InspirationFragment}
  ${MeasurementFragment}
  ${EventsListFragment}
  `;

  const ClientListFragment = gql`
  fragment ClientListFragment on Client {
    email
    fullName
    firstName
    lastName
    createdAt
    phoneNo
    eventDate
    allowSms
    appointments {
      id
      appointmentType
      dressType
      availabilityFrom
      status
      usersAvailability {
        id
        status
        user {
          id
          firstName
        }
      }
    }
    events {
      id
      gid
      eventTitle
      eventDate
      eventType
      assignedPerson
      statusCategory
    }
    orders {
      id
      totalPrice
      name
    }
  }
  `;
  const ClientConnectionGql = gql`
  query clients($first: Int, $last: Int, $after: String, $before: String, $filters: FiltersInput) {
  clients(first: $first, last: $last, after: $after, before: $before, filters: $filters) {
    edges {
      node {
        id
        gid
        ...ClientListFragment
      }
    }
    pageInfo {
      ...PageInfoFragment
    }
  }
}
${ClientListFragment}
${PageInfoFragment}
  `;

  const ClientConnectionSearchGql = gql`
  query clients($first: Int, $last: Int, $after: String, $before: String, $filters: FiltersInput) {
  clients(first: $first, last: $last, after: $after, before: $before, filters: $filters) {
    edges {
      node {
        id
        gid
        email
        firstName
        lastName
      }
    }
  }
}
  `;

  const CreateClientMutationGql = gql`
mutation createClient($input: ClientInput!) {
  createClient(input: $input) {
    client {
      id
      gid
      ...ClientFragment
    }
  }
}
${ClientFragment}
  `;

  const UpdateClientMutationGql = gql`
mutation updateClient($input: ClientInput!) {
  updateClient(input: $input) {
    client {
      id
      gid
      ...ClientFragment
    }
  }
}
${ClientFragment}
  `;

  const NewClientGql = gql`
query {
  newClient {
      ...ClientFragment
  }
}  
${ClientFragment}
  `;

  const ClientNodeGql = gql`
  query clientNode($gid: ID!) {
    node(gid: $gid) {
      id
      gid
      ...ClientFragment
    }
  }
  ${ClientFragment}
`;

  const useClientConnectionQuery = (connection) => {
    const { first, last, before, after, ...filters } = connection;
    const variables = { first, last, before, after, filters };

    return useQuery(ClientConnectionGql, {
      variables,
      fetchPolicy: 'network-only',
    });
  };

  const useClientConnectionSearchQuery = (connection) => {
    const { first, last, before, after, ...filters } = connection;
    const variables = { first, last, before, after, filters };

    return useQuery(ClientConnectionSearchGql, {
      variables,
      fetchPolicy: 'network-only',
    });
  };

  const useCreateClientMutation = (input) => useMutation(CreateClientMutationGql, {
    variables: { input },
  });

  const useUpdateClientMutation = (input) => useMutation(UpdateClientMutationGql, {
    variables: { input },
  });

  const useNewClientQuery = () => useQuery(NewClientGql, {
    fetchPolicy: 'network-only',
  });

  const useClientNodeQuery = (gid) => useQuery(ClientNodeGql, {
    variables: { gid },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const useClientMutation = (input) => (input.id ? useUpdateClientMutation(input) : useCreateClientMutation(input));

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const useClientQuery = (isNewClient, gid) => (isNewClient ? useNewClientQuery() : useClientNodeQuery(gid));

  return {
    useClientConnectionQuery,
    useClientConnectionSearchQuery,
    useClientMutation,
    useClientQuery,
    ClientFragment,
    ClientReducedFragment,
    ClientNameOnlyFragment,
  };
};
